import React from "react";
import { MarketingShell, Container, Eyebrow, useSeo, FONT } from "./Marketing";

const SECTIONS = [
  {
    n: 1, title: "About this Privacy Policy",
    body: [
      "This Privacy Policy explains how Prime Gym Turf LTD, trading as AI Turf Designer (\u201cAI Turf Designer\u201d, \u201cwe\u201d, \u201cus\u201d or \u201cour\u201d), collects, uses, stores and protects personal information when you use the AI Turf Designer application, website or related services. AI Turf Designer provides an online turf design and configuration platform that allows users to create, customise and order bespoke gym turf and related products. We are committed to protecting personal information and processing it fairly, lawfully and transparently.",
    ],
  },
  {
    n: 2, title: "Who is responsible for your personal data?",
    body: [
      "Prime Gym Turf LTD is the provider of AI Turf Designer and is responsible for personal information associated with the operation of the application and its services. Where AI Turf Designer receives customer information from a Shopify merchant in connection with an order or configured turf design, that information may be processed on behalf of the Shopify merchant. In those circumstances, the Shopify merchant may remain the data controller for its customers' personal information and Prime Gym Turf LTD may act as a data processor/service provider. If you are a customer of a business using AI Turf Designer, you may also contact that business about how it uses your personal information.",
    ],
  },
  {
    n: 3, title: "Information we may collect",
    body: ["Depending on how you use AI Turf Designer, we may process:"],
    bullets: [
      "Merchant information such as name, business name, email address, contact details, Shopify store information, store identifiers, app installation information, account/subscription information and support communications.",
      "Customer and order information supplied through a connected Shopify store, such as customer name, email address, billing address, delivery address, order reference, order value and information needed to fulfil an order.",
      "Turf design information such as dimensions, area, lanes, layout, turf colour, markings, marking colour and interval, logos, graphics, custom text, borders, design references, design previews and turnaround/production selections.",
      "Uploaded logos, artwork and other files supplied by users for the purpose of creating turf designs.",
      "Technical information such as IP address, browser/device information and application logs where required for security, troubleshooting and service operation.",
    ],
  },
  {
    n: 4, title: "Uploaded images and logos",
    body: [
      "Users may upload logos, artwork or other images when creating a turf design. These files are processed to provide design functionality, generate previews and, where applicable, support production and fulfilment. Users should not upload personal information or images relating to other individuals unless they have the appropriate rights or permission to do so. We do not sell uploaded customer logos or artwork or use them for unrelated advertising.",
    ],
  },
  {
    n: 5, title: "How we use personal information",
    bullets: [
      "Providing and operating AI Turf Designer.",
      "Creating and saving turf designs.",
      "Calculating and displaying prices.",
      "Processing configured Shopify orders.",
      "Communicating with merchants and customers about orders or support.",
      "Providing customer support.",
      "Maintaining and securing the application.",
      "Preventing fraud, abuse and unauthorised access.",
      "Maintaining appropriate technical and security records.",
      "Meeting legal and regulatory obligations.",
      "Improving the reliability and functionality of the service.",
    ],
    after: ["We do not sell personal information and do not use Shopify customer information for unrelated advertising or behavioural profiling."],
  },
  {
    n: 6, title: "AI and automated processing",
    body: [
      "AI Turf Designer uses OpenAI technology to assist with the creation of AI-generated turf design images and concepts. Information and uploaded artwork necessary to generate an AI design may be sent to OpenAI's API for processing. AI Turf Designer's AI functionality is used to assist with design creation. It does not make decisions about individuals that have legal or similarly significant effects on them. OpenAI states that data submitted to its API is not used to train its models by default unless the customer has explicitly opted in. OpenAI also states that API inputs and outputs are removed from its systems after 30 days unless legally required to retain them, subject to applicable service terms and controls.",
    ],
  },
  {
    n: 7, title: "Legal bases for processing",
    body: ["Where UK data protection law applies, we rely on appropriate lawful bases, including:"],
    bullets: [
      "Contract, where processing is necessary to provide the service, administer an account or process an order.",
      "Legitimate interests, where necessary to operate and secure the application, prevent fraud and abuse, provide support and improve reliability, taking account of individuals' rights and interests.",
      "Legal obligation, where processing is necessary to comply with legal, accounting, tax or regulatory requirements.",
      "Consent, where consent is required by law.",
    ],
  },
  {
    n: 8, title: "Information obtained from Shopify",
    body: [
      "When AI Turf Designer is installed and used by a Shopify merchant, information may be received from Shopify in accordance with the permissions granted to the application. This can include merchant/store information and relevant customer or order information required for approved application functionality. Shopify customer information is processed only for purposes connected with providing the application and configured order functionality.",
    ],
  },
  {
    n: 9, title: "Sharing personal information",
    body: [
      "We may share personal information with carefully selected service providers where necessary to operate AI Turf Designer. These may include providers supporting cloud hosting, database hosting, backups, application security, email delivery, Shopify integration/order processing and AI/design processing. OpenAI is a third-party processor/service provider used for AI image generation. OpenAI's business/API services provide encryption in transit and at rest and state that business/API data is not used for model training by default. Service providers are permitted to process personal information only as necessary to provide their services and are subject to appropriate contractual and security requirements where applicable. We do not sell personal information. We may disclose information where required by law, legal process, court order or regulatory requirement, or where necessary to protect our rights, users or systems.",
    ],
  },
  {
    n: 10, title: "Hosting and infrastructure",
    body: [
      "AI Turf Designer's production infrastructure uses managed cloud infrastructure and MongoDB Atlas for database services. Production data is maintained separately from development and preview environments. Production databases use access controls and application-specific credentials. Persistent production data is encrypted at rest, database connections use encrypted connections, and backups are protected using encryption.",
    ],
  },
  {
    n: 11, title: "International data transfers",
    body: [
      "Some technology and infrastructure providers used to operate AI Turf Designer may process information outside the United Kingdom. Where personal information is transferred internationally, we take appropriate steps to ensure the transfer complies with applicable data protection law, using recognised adequacy arrangements or appropriate contractual safeguards where required.",
    ],
  },
  {
    n: 12, title: "Data security",
    body: ["We take reasonable technical and organisational measures designed to protect personal information against unauthorised access, unauthorised disclosure, accidental loss, destruction, alteration and misuse."],
    bullets: [
      "Role-based access controls: staff and administrator access is granted by role (staff/admin/owner and platform roles) and re-checked on every request, so revoked access stops working immediately.",
      "A strong password policy for staff and administrator accounts (minimum length, a mixture of character types and rejection of common/guessable passwords), account lockout after repeated failed logins, and passwords stored only as salted bcrypt hashes (never in plain text).",
      "Logging of staff/administrator access to personal data, recording who accessed what and when (without storing the personal data, passwords or secrets in the log), retained for a limited period and available only to authorised administrators.",
      "Access controls and application-specific credentials.",
      "Encryption of persistent production data at rest.",
      "Encrypted connections for data in transit.",
      "Separation of production and preview/development environments.",
      "Backup and recovery measures.",
    ],
    after: ["No internet-based service can guarantee absolute security. We continually review our security measures and take appropriate steps to protect the information we process."],
  },
  {
    n: 13, title: "Data retention",
    body: [
      "We do not retain personal information indefinitely. Personal information is retained only for as long as reasonably necessary for the purposes described in this Privacy Policy, and an automated process runs daily to anonymise information that has passed its retention period. When information reaches the end of its retention period we anonymise it — the personal fields (such as name, email address, phone number, company and address) are irreversibly overwritten while any non-personal design, configuration and aggregate information is preserved. The retention periods we currently apply are:",
    ],
    bullets: [
      "Unconverted enquiries, saved leads and marketing contact submissions: anonymised 12 months after the last meaningful activity where they have not converted into an order.",
      "Abandoned or incomplete designs, and checkouts that were never completed into an order: anonymised 6 months after the last activity.",
      "Completed/paid orders: the personal and order information required for order fulfilment, merchant records, accounting and legal obligations is retained for up to 7 years (reflecting UK accounting and tax record-keeping requirements), after which the personal information is anonymised while the non-personal order record is preserved.",
      "Transactional and notification email logs: the recipient address is anonymised after 12 months.",
    ],
    after: [
      "Retention is also determined by factors including whether the merchant continues to use the application, whether information is required for an order or fulfilment, legal/tax/accounting requirements, dispute resolution, and security and fraud prevention. Information may remain temporarily in secure backup systems after anonymisation in active systems, in accordance with applicable backup retention procedures. We may also anonymise or delete information sooner in response to a valid deletion request (see 'Data deletion' below).",
    ],
  },
  {
    n: 14, title: "Data deletion",
    body: [
      "Merchants may request deletion of personal information processed by AI Turf Designer where applicable by contacting primegymturf@gmail.com. Where AI Turf Designer processes Shopify customer information on behalf of a merchant, deletion requests may need to be handled in coordination with the relevant Shopify merchant. Information that we are legally required to retain may not be immediately deleted but will be retained only for the applicable legal or regulatory period.",
    ],
  },
  {
    n: 15, title: "Your data protection rights",
    body: ["Depending on the circumstances and applicable law, you may have rights including:"],
    bullets: [
      "Right to be informed about how your information is used.",
      "Right to request access to personal information.",
      "Right to request correction of inaccurate information.",
      "Right to request deletion.",
      "Right to request restriction of processing.",
      "Right to object to certain processing.",
      "Right to data portability where applicable.",
      "Right to withdraw consent where processing is based on consent.",
      "Rights relating to automated decision-making and profiling where applicable.",
    ],
    after: ["To exercise your rights, contact primegymturf@gmail.com. Some rights are subject to legal exceptions and may not apply in every circumstance."],
  },
  {
    n: 16, title: "Shopify privacy requests",
    body: [
      "Where AI Turf Designer processes customer information supplied by a Shopify merchant, we will cooperate with the merchant and Shopify's applicable privacy processes where required. If you are a customer of a Shopify merchant using AI Turf Designer, you may also contact the merchant directly regarding your personal information.",
    ],
  },
  {
    n: 17, title: "Cookies and technical information",
    body: [
      "AI Turf Designer and its website may use cookies, local storage or similar technologies necessary to operate the service, maintain sessions, support security, troubleshoot errors and improve application performance. Where non-essential cookies or similar technologies are used and consent is legally required, appropriate consent mechanisms will be provided.",
    ],
  },
  {
    n: 18, title: "Children's privacy",
    body: [
      "AI Turf Designer is a business-to-business and commercial design service and is not intended for use by children. We do not knowingly collect personal information from children for the purpose of providing the service.",
    ],
  },
  {
    n: 19, title: "Security incidents",
    body: [
      "We maintain a documented Security Incident Response Policy and appropriate procedures for identifying, investigating and responding to security incidents. Where a personal data breach occurs, we will assess the incident and take appropriate action in accordance with applicable data protection law, including notifying the UK Information Commissioner's Office and affected individuals or merchants where legally required.",
      "Our policy distinguishes between infrastructure/platform incidents, which are handled by Emergent and other infrastructure providers under their own security programmes, and incidents involving the AI Turf Designer application or its handling of merchant/customer data, which are handled by Prime Gym Turf LTD. We do not claim certifications or security capabilities that belong to our infrastructure providers.",
    ],
  },
  {
    n: 20, title: "Changes to this Privacy Policy",
    body: [
      "We may update this Privacy Policy to reflect changes to AI Turf Designer, the information we process, our service providers, applicable laws, or our security and retention practices. The latest version will be published at https://aiturfdesigner.com/privacy-policy.",
    ],
  },
  {
    n: 21, title: "Contact us",
    body: ["Prime Gym Turf LTD", "31 Belmont Road", "Wolverhampton", "WV4 5UD", "United Kingdom", "Email: primegymturf@gmail.com", "Website: https://aiturfdesigner.com/"],
  },
  {
    n: 22, title: "Complaints",
    body: [
      "If you have concerns about how we process your personal information, please contact us first at primegymturf@gmail.com. You also have the right to complain to the UK's data protection regulator, the Information Commissioner's Office (ICO).",
    ],
  },
];

export default function PrivacyPolicy() {
  useSeo(
    "Privacy Policy | AI Turf Designer",
    "How AI Turf Designer (Prime Gym Turf LTD) collects, uses, stores and protects personal information across its turf design application, website and Shopify integration."
  );
  return (
    <MarketingShell>
      <section className="border-b border-white/10 bg-[#0a0a0a]">
        <Container className="py-16 md:py-20">
          <Eyebrow>Legal</Eyebrow>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter text-white" style={{ fontFamily: FONT.display }} data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-4 text-zinc-500 text-sm" data-testid="privacy-updated">Last updated: 2 September 2026</p>
        </Container>
      </section>

      <section>
        <Container className="py-12 md:py-16 max-w-4xl">
          <div className="space-y-10" data-testid="privacy-content">
            {SECTIONS.map((s) => (
              <div key={s.n} data-testid={`privacy-section-${s.n}`}>
                <h2 className="text-lg md:text-xl font-bold uppercase tracking-wide text-white" style={{ fontFamily: FONT.display }}>
                  {s.n}. {s.title}
                </h2>
                <div className="mt-3 space-y-3">
                  {(s.body || []).map((p, i) => (
                    <p key={i} className="text-zinc-400 text-base leading-relaxed">{p}</p>
                  ))}
                  {s.bullets && (
                    <ul className="list-disc pl-6 space-y-2 marker:text-[#00D26A]">
                      {s.bullets.map((b, i) => <li key={i} className="text-zinc-400 text-base leading-relaxed">{b}</li>)}
                    </ul>
                  )}
                  {(s.after || []).map((p, i) => (
                    <p key={`a-${i}`} className="text-zinc-400 text-base leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Container>
      </section>
    </MarketingShell>
  );
}
